import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesCodeMessages } from "@/i18n/messages/frames-code";
import { cn } from "@/lib/cn";
import { Button, Icons } from "@/components/ui";
import { CopyFeedbackIcon } from "@/components/ui/copy-feedback-icon";
import { useCallback, useMemo, useState } from "react";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "@/components/shapes/shared/frame-chrome-config";
import { useFrameChromeHidden } from "@/components/shapes/shared/frame-renderer-context";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useAuthoredMotion } from "@/components/shapes/shared/use-authored-motion";
import { CodeWindow } from "@/components/shapes/shared/card-visual-parts";
import { CARD_BODY_CONTENT } from "@/components/shapes/shared/frame-content-padding";
import { codeFrameLanguage, codeFrameText } from "./code-content";
import { copyToClipboard } from "@/lib/copy-to-clipboard";

/**
 * Code, opened as its card (Part 10): the dark code window with its title
 * line (the file's title · its language), then every line. Copy sits in
 * the window's hover pill, or top right on hover where no window hosts it.
 */
export function CodeFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesCodeMessages);
  const tc = useT(commonMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const code = codeFrameText(content);
  const authoredMotion = useAuthoredMotion(code, data.status === "streaming");
  const language = codeFrameLanguage(content);
  const title = data.title.trim();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!code) return;
    void copyToClipboard(code).then((done) => {
      if (!done) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [code]);
  const chromeConfig = useMemo<FrameChromeConfig>(
    () => ({
      tools: [
        {
          kind: "button",
          id: "code-copy",
          label: copied ? tc("copied") : t("copyCode"),
          icon: copied ? Icons.Check : Icons.Copy,
          onSelect: handleCopy,
          disabled: !code,
        },
      ],
    }),
    [code, copied, handleCopy, t, tc],
  );
  const copyInPill = useFrameChromeConfig(chromeConfig);
  const chromeHidden = useFrameChromeHidden();

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="code-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn("nodrag min-h-0 flex-1", CARD_BODY_CONTENT)}
        data-frame-content="true"
      >
        <CodeWindow
          density="frame"
          className={cn("scrollbar-none overflow-auto", selected && "nowheel")}
        >
          <p
            className="truncate text-[color:var(--gadget-code-muted)]"
            data-testid="code-frame-title"
          >
            {title ? `${title} · ${language}` : language}
          </p>
          <pre
            className="authored-motion-code whitespace-pre font-mono"
            data-authored-motion-active={
              authoredMotion.active ? "true" : "false"
            }
            data-authored-motion-cycle={authoredMotion.cycle}
            data-testid="code-authored-motion"
          >
            <code>
              {code || (
                <span className="italic text-[color:var(--gadget-code-muted)]">
                  {t("noCode")}
                </span>
              )}
              {authoredMotion.active && (
                <span
                  className="ml-0.5 inline-block h-[1.1em] w-[2px] animate-pulse bg-[var(--gadget-code-accent)] align-text-bottom"
                  aria-label={t("writingCursor")}
                />
              )}
            </code>
          </pre>
        </CodeWindow>
      </div>
      {!copyInPill && !chromeHidden && (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={handleCopy}
          className="absolute right-3 top-3 z-10 text-[color:var(--gadget-code-muted)] opacity-0 transition-opacity hover:text-[color:var(--gadget-code-ink)] focus-visible:opacity-100 group-hover:opacity-100"
          data-testid="code-frame-copy-btn"
          aria-label={t("copyCode")}
        >
          <CopyFeedbackIcon copied={copied} className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
